import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box, Container, Typography, Grid, Card, CardContent, Chip, Button,
  Fade, Paper, CircularProgress, Alert, Tooltip, TextField, FormControl,
  InputLabel, Select, MenuItem, InputAdornment,
} from '@mui/material';
import {
  CalendarToday, MenuBook, TrendingUp, Search, Refresh, ArrowForward,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { fetchKeywords, fetchStats } from '../utils/api';
import { colors } from '../styles/theme';

// ─── 词云组件 ──────────────────────────────────────────────
function KeywordCloud({ keywords, onKeywordClick }) {
  if (keywords.length === 0) return null;
  const maxCount = Math.max(...keywords.map(k => k.total_count || 1));
  const minCount = Math.min(...keywords.map(k => k.total_count || 1));
  const getFontSize = (count) => {
    if (maxCount === minCount) return 22;
    return 14 + ((count - minCount) / (maxCount - minCount)) * 22;
  };
  const getColor = (index) => {
    const palette = [
      colors.primary.main, colors.primary.light, colors.secondary.main,
      '#4A7C6F', '#A68820', '#6B7280', '#2D5A4A', '#8B6914',
    ];
    return palette[index % palette.length];
  };
  return (
    <Box sx={{ display:'flex', flexWrap:'wrap', gap:2, justifyContent:'center', alignItems:'center', p:3, minHeight:140 }}>
      {keywords.map((kw, i) => (
        <Tooltip key={kw.id} title={`${kw.total_count||0} 条语料 · ${kw.year}年`} arrow>
          <Typography onClick={() => onKeywordClick(kw)} sx={{
            fontSize: `${getFontSize(kw.total_count||1)}px`, color: getColor(i),
            fontWeight:600, cursor:'pointer', transition:'all 0.25s ease',
            fontFamily:'"Noto Serif SC", serif',
            '&:hover':{ transform:'scale(1.15)', textShadow:'0 2px 8px rgba(0,0,0,0.2)' },
          }}>{kw.chinese_name}</Typography>
        </Tooltip>
      ))}
    </Box>
  );
}

// ─── 搜索候选词 ──────────────────────────────────────────
function SearchBox({ keywords, onSelect }) {
  const [value, setValue] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const suggestions = value.length >= 1
    ? keywords.filter(k =>
        k.chinese_name.includes(value) ||
        (k.english_translations||'').toLowerCase().includes(value.toLowerCase())
      ).slice(0,8)
    : [];
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  return (
    <Box ref={ref} sx={{ position:'relative', width:'100%', maxWidth:400 }}>
      <TextField fullWidth size="small" placeholder="搜索热词或英文翻译..." value={value}
        onChange={(e)=>{setValue(e.target.value);setOpen(true);}}
        onFocus={()=>value&&setOpen(true)}
        InputProps={{ startAdornment:(<InputAdornment position="start"><Search sx={{color:colors.primary.main,fontSize:20}}/></InputAdornment>) }}
      />
      {open && suggestions.length > 0 && (
        <Paper sx={{ position:'absolute',top:'100%',left:0,right:0,zIndex:1300,
          maxHeight:320,overflow:'auto',mt:0.5,boxShadow:'0 8px 24px rgba(0,0,0,0.12)',borderRadius:2 }}>
          {suggestions.map(kw => (
            <Box key={kw.id} onClick={()=>{setValue('');setOpen(false);onSelect(kw);}}
              sx={{ px:2,py:1.5,cursor:'pointer',borderBottom:`1px solid ${colors.accent.cool}`,
                '&:hover':{backgroundColor:colors.accent.highlight},'&:last-child':{borderBottom:'none'} }}>
              <Box sx={{display:'flex',alignItems:'center',gap:1}}>
                <Typography variant="body2" sx={{fontWeight:600,fontFamily:'"Noto Serif SC",serif'}}>{kw.chinese_name}</Typography>
                <Chip label={`${kw.year}`} size="small" sx={{height:18,fontSize:'0.7rem',backgroundColor:colors.accent.warm}}/>
                <Typography variant="caption" sx={{ml:'auto',color:'text.secondary'}}>{kw.total_count||0} 条</Typography>
              </Box>
              {kw.english_translations && (
                <Typography variant="caption" sx={{color:'text.secondary',fontStyle:'italic'}}>
                  {kw.english_translations.split('/')[0].trim()}
                </Typography>
              )}
            </Box>
          ))}
        </Paper>
      )}
    </Box>
  );
}

// ─── 主页面 ────────────────────────────────────────────────
function Home() {
  const navigate = useNavigate();
  const [allKeywords, setAllKeywords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({});
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [kws, st] = await Promise.all([fetchKeywords({}), fetchStats()]);
        setAllKeywords(kws);
        setStats(st);
      } catch (e) {
        setError('加载数据失败');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const availableYears = useMemo(() =>
    [...new Set(allKeywords.map(k => k.year))].filter(Boolean).sort((a,b)=>b-a),
    [allKeywords]);

  // 统一筛选：词云和卡片共用同一份数据
  const filteredKeywords = useMemo(() =>
    allKeywords.filter(k => {
      const matchYear = selectedYear === null || k.year === selectedYear;
      const matchCat = !selectedCategory || k.category === selectedCategory;
      return matchYear && matchCat;
    }),
    [allKeywords, selectedYear, selectedCategory]);

  const handleReset = () => { setSelectedYear(null); setSelectedCategory(''); };
  const handleKeywordClick = (kw) => navigate(`/keyword/${kw.id}`);
  const isFiltered = selectedYear !== null || selectedCategory !== '';

  return (
    <Box sx={{ minHeight:'100vh', backgroundColor:colors.background.default, pb:8 }}>
      {/* Hero */}
      <Box sx={{ background:`linear-gradient(135deg, ${colors.primary.dark} 0%, ${colors.primary.main} 50%, ${colors.primary.light} 100%)`, color:'white', py:{xs:4,md:5} }}>
        <Container maxWidth="lg">
          <Fade in timeout={600}>
            <Box textAlign="center">
              <Typography variant="h1" sx={{ fontSize:{xs:'1.6rem',md:'2.5rem'}, fontWeight:700, mb:1.5, fontFamily:'"Noto Serif SC",serif' }}>
                中文热词国际新闻报道检索系统
              </Typography>
              <Typography variant="body1" sx={{ opacity:0.85, mb:3, maxWidth:500, mx:'auto' }}>
                2014–2025 年十大中文热词的国际新闻语料分析
              </Typography>
              <Box sx={{ display:'flex', justifyContent:'center', gap:3, flexWrap:'wrap' }}>
                {[
                  {icon:<MenuBook sx={{fontSize:18}}/>, label:`${(stats.total_corpus||0).toLocaleString()} 条语料`},
                  {icon:<TrendingUp sx={{fontSize:18}}/>, label:`${stats.keyword_count||0} 个热词`},
                  {icon:<CalendarToday sx={{fontSize:18}}/>, label:`${availableYears.length} 年数据`},
                ].map(({icon,label})=>(
                  <Paper key={label} sx={{ px:2.5, py:1, backgroundColor:'rgba(255,255,255,0.15)', display:'flex', alignItems:'center', gap:1, color:'white' }}>
                    {icon}<Typography variant="body2">{label}</Typography>
                  </Paper>
                ))}
              </Box>
            </Box>
          </Fade>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt:-3, position:'relative', zIndex:10 }}>
        {/* 搜索条 */}
        <Paper sx={{ p:3, mb:3, borderRadius:3, boxShadow:'0 8px 32px rgba(0,0,0,0.10)',
          display:'flex', gap:2, flexWrap:'wrap', alignItems:'center' }}>
          <SearchBox keywords={allKeywords} onSelect={handleKeywordClick} />
          <FormControl size="small" sx={{minWidth:140}}>
            <InputLabel>类别</InputLabel>
            <Select value={selectedCategory} label="类别" onChange={(e)=>setSelectedCategory(e.target.value)}>
              <MenuItem value="">全部类别</MenuItem>
              {(stats.categories||[]).map(cat=><MenuItem key={cat} value={cat}>{cat}</MenuItem>)}
            </Select>
          </FormControl>
          {isFiltered && (
            <Button variant="text" size="small" startIcon={<Refresh/>} onClick={handleReset} sx={{color:colors.primary.main}}>重置</Button>
          )}
          <Typography variant="body2" sx={{ ml:'auto', color:'text.secondary' }}>
            {filteredKeywords.length} / {allKeywords.length} 个热词
          </Typography>
        </Paper>

        {loading ? (
          <Box sx={{display:'flex',justifyContent:'center',py:10}}><CircularProgress size={48}/></Box>
        ) : error ? (
          <Alert severity="error" sx={{mb:3}}>{error}</Alert>
        ) : (
          <>
            {/* 年份 pill */}
            <Box sx={{ mb:3, display:'flex', gap:1, flexWrap:'wrap', justifyContent:'center' }}>
              <Chip label="全部年份" onClick={()=>setSelectedYear(null)}
                variant={selectedYear===null?'filled':'outlined'}
                sx={{ fontWeight:600,
                  backgroundColor:selectedYear===null?colors.primary.main:'transparent',
                  color:selectedYear===null?'white':'text.primary',
                  '&:hover':{backgroundColor:selectedYear===null?colors.primary.dark:colors.accent.cool} }} />
              {availableYears.map(year=>{
                const count = allKeywords.filter(k=>k.year===year).length;
                return (
                  <Chip key={year} label={`${year}（${count}）`}
                    onClick={()=>setSelectedYear(selectedYear===year?null:year)}
                    variant={selectedYear===year?'filled':'outlined'}
                    sx={{ fontWeight:selectedYear===year?700:400,
                      backgroundColor:selectedYear===year?colors.primary.main:'transparent',
                      color:selectedYear===year?'white':'text.primary',
                      '&:hover':{backgroundColor:selectedYear===year?colors.primary.dark:colors.accent.cool} }} />
                );
              })}
            </Box>

            {/* 词云 */}
            <Paper sx={{ p:3, mb:3, borderRadius:3, boxShadow:'0 4px 20px rgba(0,0,0,0.06)' }}>
              <Typography variant="h6" sx={{fontWeight:600,mb:0.5,textAlign:'center'}}>热词云</Typography>
              <Typography variant="caption" color="text.secondary" sx={{display:'block',textAlign:'center',mb:1}}>
                字号代表语料数量，点击进入详情
              </Typography>
              {filteredKeywords.length > 0 ? (
                <KeywordCloud keywords={filteredKeywords} onKeywordClick={handleKeywordClick}/>
              ) : (
                <Box sx={{textAlign:'center',py:4}}>
                  <Typography color="text.secondary" sx={{mb:2}}>无匹配热词</Typography>
                  <Button variant="outlined" size="small" onClick={handleReset} startIcon={<Refresh/>}>重置筛选</Button>
                </Box>
              )}
            </Paper>

            {/* 热词卡片 */}
            <Typography variant="h6" sx={{fontWeight:600,mb:2}}>
              {selectedYear?`${selectedYear} 年热词`:'全部热词'}
              <Chip label={`${filteredKeywords.length} 个`} size="small" sx={{ml:1,backgroundColor:colors.accent.warm,fontWeight:600}}/>
            </Typography>
            {filteredKeywords.length > 0 && (
              <Grid container spacing={2}>
                {filteredKeywords.map(kw=>(
                  <Grid item xs={12} sm={6} md={4} lg={3} key={kw.id}>
                    <Card onClick={()=>handleKeywordClick(kw)} sx={{
                      cursor:'pointer', height:'100%', transition:'all 0.25s ease',
                      '&:hover':{boxShadow:'0 8px 24px rgba(0,0,0,0.12)',transform:'translateY(-2px)'},
                    }}>
                      <CardContent sx={{p:2}}>
                        <Box sx={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',mb:0.5}}>
                          <Typography sx={{fontWeight:700,fontFamily:'"Noto Serif SC",serif',fontSize:'1rem'}}>{kw.chinese_name}</Typography>
                          <Chip label={`${kw.total_count||0}`} size="small" sx={{backgroundColor:colors.accent.warm,flexShrink:0,ml:1,height:22,fontSize:'0.75rem'}}/>
                        </Box>
                        <Typography variant="body2" sx={{
                          color:colors.primary.main,fontStyle:'italic',fontFamily:'"Playfair Display",serif',
                          fontSize:'0.8rem',lineHeight:1.4,mb:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',
                        }}>{kw.english_translations?.split('/')[0].trim()||'暂无翻译'}</Typography>
                        <Box sx={{display:'flex',gap:0.5,flexWrap:'wrap'}}>
                          <Chip label={`${kw.year}年`} size="small" variant="outlined" sx={{height:20,fontSize:'0.7rem'}}/>
                          {kw.category && <Chip label={kw.category} size="small" variant="outlined" sx={{height:20,fontSize:'0.7rem'}}/>}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}
      </Container>
    </Box>
  );
}

export default Home;
